import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import {
  cognito,
  queryClient,
  useAuthStore,
  useThemeStore,
} from "@theprelude/core";
import { LoadingPage } from "@theprelude/ds";
import "@theprelude/ds/system.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { analytics } from "@/lib/analytics";
import { Auth, useAuth } from "@/lib/auth";
import { isLocal, isUs1, isEu1 } from "@/lib/env";
import { AppErrorPage, NotFoundPage } from "./components/app-shell/app-shell";
import { useThreatIntelStore } from "./hooks/threat-intel-store";
import { routeTree } from "./routeTree.gen";
import "./styles/index.css";
import "./styles/theme.css";

function initCognito(
  clientId: string,
  redirectURL: string = window.location.origin,
) {
  const cognitoUserPoolId =
    localStorage.getItem("cognitoUserPoolId") ??
    import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const cognitoUserPoolClientId = clientId;
  const cognitoOAuthDomain =
    (localStorage.getItem("cognitoOAuthDomain") as string | null) ??
    import.meta.env.VITE_COGNITO_OAUTH_DOMAIN;

  cognito.init({
    Cognito: {
      userPoolId: cognitoUserPoolId,
      userPoolClientId: cognitoUserPoolClientId,
      loginWith: {
        email: true,
        oauth: {
          domain: cognitoOAuthDomain,
          scopes: ["email", "openid", "profile"],
          redirectSignIn: [redirectURL],
          redirectSignOut: [window.location.origin],
          responseType: "code",
          providers: ["Google"],
        },
      },
    },
  });
}

const isCLIAuth = window.location.pathname === "/cli-auth";
const isCLIToken = window.location.pathname === "/cli-token";

if (!isCLIToken) {
  if (isCLIAuth) {
    initCognito(
      localStorage.getItem("cognitoUserPoolCliClientId") ??
        import.meta.env.VITE_COGNITO_USER_POOL_CLI_CLIENT_ID,
      `${window.location.origin}/cli-token`,
    );
  } else {
    initCognito(
      localStorage.getItem("cognitoUserPoolClientId") ??
        import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
    );
  }
}

analytics.inject();

if (!isLocal && !isUs1 && !isEu1) {
  Sentry.init({
    dsn: "https://22c17954003506c31131ee3c91681d86@o4508044036145152.ingest.us.sentry.io/4508049164206080",
    environment: import.meta.env.VITE_PRELUDE_ENVIRONMENT,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Tracing
    tracesSampleRate: 1, //  Capture 100% of the transactions
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    profilesSampleRate: 1,
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

if (!isLocal) {
  analytics.init();

  // disable identify for EU1 because of GDPR
  if (!isEu1) {
    // incase the user switches accounts
    useAuthStore.subscribe(({ currentAccount, session }) => {
      const handle = session?.tokens?.idToken?.payload.email as string;
      analytics.identify(currentAccount, handle);
    });
  }
}

const router = createRouter({
  routeTree,
  context: {
    queryClient,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    auth: undefined!,
  },
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: AppErrorPage,
  defaultPendingComponent: LoadingPage,
  defaultNotFoundComponent: NotFoundPage,
});

/**
 * HubSpot call-to-action triggers don't play nicely with single page apps. As a
 * workaround, we can force HubSpot to find relevant CTAs on every route change.
 * REF: https://community.hubspot.com/t5/APIs-Integrations/Force-CTA-Scripts-to-Run-on-History-Change-for-single-page/m-p/87956
 *
 * Note that this solution will only work if the CTA is not on the first page due
 * an undesirable issue with @tanstack/react-router.
 * REF: https://github.com/TanStack/router/issues/3676
 */
router.subscribe("onLoad", () => {
  analytics.refresh();
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

useThemeStore.getState().setTheme();
useThreatIntelStore.getState().clearExpiredEdits();

export interface RouterContext {
  queryClient: QueryClient;
  auth: Auth;
}

function App() {
  const auth = useAuth();

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        auth,
      }}
    />
  );
}

const rootElement = document.querySelector("#root") as HTMLElement;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      /* eslint-disable no-console */
      console.warn("Uncaught error:", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });

  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

// This is a hack to expose the service function to the window object in the browser console
// so that we can test it locally. ,This will not be included in the production build.
declare global {
  interface Window {
    service: any;
  }
}
